import { Button } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ChevronLeftIcon } from '@heroicons/react/24/outline';
import { AuthWrapper, DynamicForm } from '@src/components';
import { supportSchema } from '@src/formSchemas/support';
import { useSupportForm } from './hooks';
import { SupportSuccess } from './components';
import { SupportTicketParams } from '@src/services/support/supportApiTypes';
import { useDynamicFormState } from '@src/hooks';

export const Support = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const {
        formRef,
        isDirty,
        isValid,
        isValidating,
        handleDataChange,
        handleSubmit,
    } = useDynamicFormState();

    const { onSubmit, isLoading, isSubmitted, resetForm } = useSupportForm();

    const handleSubmitAnother = () => {
        resetForm();
    };

    if (isSubmitted) {
        return (
            <AuthWrapper>
                <div className="max-w-2xl mx-auto">
                    <div className="flex items-center mb-6">
                        <Button
                            onClick={() => navigate(-1)}
                            className="w-8 h-8 flex items-center justify-center p-0 mr-4"
                        >
                            <ChevronLeftIcon className="w-4 h-4" />
                        </Button>
                        <h1 className="text-2xl font-semibold text-gray-900">
                            {t('pageTitle.support')}
                        </h1>
                    </div>
                    <SupportSuccess onSubmitAnother={handleSubmitAnother} />
                </div>
            </AuthWrapper>
        );
    }

    return (
        <AuthWrapper>
            <div className="max-w-2xl mx-auto">
                <div className="flex items-center mb-6">
                    <Button
                        onClick={() => navigate(-1)}
                        className="w-8 h-8 flex items-center justify-center p-0 mr-4"
                    >
                        <ChevronLeftIcon className="w-4 h-4" />
                    </Button>
                    <h1 className="text-2xl font-semibold text-gray-900">
                        {t('pageTitle.support')}
                    </h1>
                </div>

                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="mb-6">
                        <h2 className="text-lg font-medium text-gray-900 mb-2">
                            {t('forms.support.thankYou')}
                        </h2>
                        <p className="text-gray-600 text-sm">
                            {t('forms.support.description')}
                        </p>
                    </div>

                    <DynamicForm
                        ref={formRef}
                        useFormOptions={{
                            mode: 'onChange',
                            reValidateMode: 'onChange',
                        }}
                        formKeyName="supportForm"
                        onHandleSubmit={(data: Record<string, any>) => {
                            onSubmit(data as SupportTicketParams);
                        }}
                        formRows={supportSchema({ t })}
                        containerClassNames="grid gap-6"
                        hasDataChanged={handleDataChange}
                        formFooter={
                            <div className="flex justify-end pt-6">
                                <Button
                                    type="primary"
                                    htmlType="button"
                                    size="large"
                                    loading={isLoading}
                                    onClick={() => handleSubmit(onSubmit)}
                                    disabled={
                                        !isDirty || !isValid || isValidating
                                    }
                                    className="btn-primary min-w-32"
                                >
                                    {isLoading
                                        ? t('buttons.submitting')
                                        : t('buttons.submit')}
                                </Button>
                            </div>
                        }
                    />
                </div>
            </div>
        </AuthWrapper>
    );
};
